/**
 * PhaseCoach — "what do I do right now?" banner.
 *
 * Sits directly beneath the StatusBar and tells the player exactly
 * which action is expected at this moment. Pure projection of state.
 *
 * Tuned for the simplified-play loop in `routes/Play.tsx`: the client
 * auto-pilots draft-plan / document-deploy / incident-fortify, so the
 * player only ever drives `execute-tests`. The banner intentionally
 * stays silent during the auto-pilot windows — flashing "drag a card"
 * for ~200ms before it disappears would mislead, not help.
 *
 * Priority of guidance (highest first):
 *   1. Combat sub-state       → context-specific copy from AttackState
 *   2. Not your turn          → "{Name}'s turn — they're attacking…"
 *   3. Your turn, attack-tests → "Click your territory → click an enemy"
 *   4. Anything else          → no banner (auto-pilot is moving you on)
 *
 * On the auto-piloted phases (draft-plan, document-deploy, incident-fortify)
 * the primary line is the forward-looking attack hint, AND a small mono
 * `nowLine` is rendered beneath it that names what the game is currently
 * doing on the player's behalf — e.g. "Defense Phase — wrapping up your
 * turn, auto-progressing." This was added because first-time players
 * couldn't tell whether the game was advancing or had stalled during the
 * 200-1500ms autopilot windows.
 */

import type { ReactNode } from 'react';
import type {
  AttackState,
  DashboardState,
  TurnPhase,
} from '@qd/protocol';
import { fontStacks } from '@qd/design-tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

export const PHASE_COACH_HEIGHT = 40;
// On mobile the long tutorial copy ("Stronger territories make stronger
// attacks", etc.) wraps to 3-4 lines at 375px wide. Allocate a taller
// reservation so the bottom of the message is never clipped, and let
// MobilePlayLayout offset the map content by this larger value.
export const PHASE_COACH_HEIGHT_MOBILE = 80;

interface PhaseCoachProps {
  readonly isMyTurn: boolean;
  readonly currentPlayerName: string | null;
  readonly phase: TurnPhase;
  readonly attackState: AttackState;
  /** Display name of the attacking territory, used in combat coaching. Empty when irrelevant. */
  readonly sourceName: string;
  /** Display name of the target territory, used in combat coaching. Empty when irrelevant. */
  readonly targetName: string;
  /** Kept for API symmetry with the deeper-mode roadmap; unused in simplified play. */
  readonly dashboard: DashboardState | null;
  /** Reserve cube count for the self player. Used in rescue-deploy coaching. */
  readonly reserveCubes: number;
  /** Pixel offset from the top of the viewport. Default 56 (under desktop StatusBar). */
  readonly topOffsetPx?: number;
  /**
   * True when the self player is in execute-tests, idle, has no legal
   * attack from any owned territory, and HAS reserves. The coach copy
   * tells them to tap an owned territory to drop a cube.
   */
  readonly inRescueDeployMode?: boolean;
  /**
   * True when the self player is in execute-tests, idle, has no legal
   * attack, and ALSO has zero reserves. The coach prompts End Turn.
   */
  readonly isHardStuck?: boolean;
}

interface CoachMessage {
  readonly primary: string;
  readonly secondary?: string;
  readonly tone: 'wait' | 'act' | 'combat';
  /**
   * Optional secondary line, rendered beneath `primary`, that names the
   * phase happening RIGHT NOW. Used during the auto-piloted phases
   * (draft-plan / document-deploy / incident-fortify) so the player
   * understands not just "what they'll do next" (the forward-looking
   * primary line) but also "what the game is doing for them in this
   * moment". Empty/undefined on execute-tests, where the primary line
   * already describes the current action.
   */
  readonly nowLine?: string;
}

export function PhaseCoach({
  isMyTurn,
  currentPlayerName,
  phase,
  attackState,
  sourceName,
  targetName,
  dashboard,
  reserveCubes,
  topOffsetPx = 56,
  inRescueDeployMode = false,
  isHardStuck = false,
}: PhaseCoachProps): ReactNode {
  void dashboard;
  const isMobile = useIsMobile();
  const message = computeMessage({
    isMyTurn,
    currentPlayerName,
    phase,
    attackState,
    sourceName,
    targetName,
    inRescueDeployMode,
    isHardStuck,
    reserveCubes,
  });
  if (!message) return null;

  const accent =
    message.tone === 'combat'
      ? '#f87171'  // red-400
      : message.tone === 'act'
        ? '#60a5fa' // blue-400
        : '#94a3b8'; // slate-400

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="What to do now"
      style={{
        position: 'fixed',
        top: topOffsetPx,
        left: 0,
        right: 0,
        // Mobile: allocate a taller height so wrapped tutorial copy ("Stronger
        // territories make stronger attacks", etc.) isn't clipped at 375px.
        // Desktop fits the same copy in 1-2 lines so the original 40px holds.
        height: isMobile ? PHASE_COACH_HEIGHT_MOBILE : PHASE_COACH_HEIGHT,
        zIndex: 7,
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 10 : 14,
        padding: isMobile ? '8px 14px' : '0 22px',
        background: 'rgba(10, 20, 50, 0.97)',
        borderBottom: `1px solid ${accent}88`,
        color: '#f1f5f9',
        fontFamily: fontStacks.body,
        fontSize: isMobile ? 12.5 : 13,
        overflow: 'hidden',
      }}
    >
      <Marker tone={message.tone} />
      <span
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          lineHeight: 1.3,
          minWidth: 0,
        }}
      >
        <span>{message.primary}</span>
        {message.nowLine ? (
          <span
            style={{
              fontFamily: fontStacks.mono,
              fontSize: 10.5,
              letterSpacing: '0.04em',
              color: '#94a3b8',
              lineHeight: 1.3,
            }}
          >
            {message.nowLine}
          </span>
        ) : null}
      </span>
      {message.secondary ? (
        <span
          style={{
            fontFamily: fontStacks.mono,
            fontSize: 10,
            letterSpacing: '0.14em',
            color: accent,
            opacity: 0.9,
            border: `1px solid ${accent}55`,
            borderRadius: 4,
            padding: '3px 8px',
            whiteSpace: 'nowrap',
            background: `${accent}18`,
          }}
        >
          {message.secondary}
        </span>
      ) : null}
    </div>
  );
}

function Marker({ tone }: { tone: CoachMessage['tone'] }): ReactNode {
  const dot =
    tone === 'combat'
      ? '#f87171'
      : tone === 'act'
        ? '#60a5fa'
        : '#94a3b8';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: dot,
        boxShadow: tone === 'act' ? `0 0 6px #60a5fa` : undefined,
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// Message resolver
// -----------------------------------------------------------------------------

interface ResolverArgs {
  readonly isMyTurn: boolean;
  readonly currentPlayerName: string | null;
  readonly phase: TurnPhase;
  readonly attackState: AttackState;
  readonly sourceName: string;
  readonly targetName: string;
  readonly inRescueDeployMode: boolean;
  readonly isHardStuck: boolean;
  readonly reserveCubes: number;
}

function computeMessage(args: ResolverArgs): CoachMessage | null {
  const {
    isMyTurn,
    currentPlayerName,
    phase,
    attackState,
    sourceName,
    targetName,
    inRescueDeployMode,
    isHardStuck,
    reserveCubes,
  } = args;

  // Combat speaks for itself regardless of whose turn it is — when combat
  // is live the modal is visible to everyone, so the banner mirrors it.
  const combat = combatCopy(attackState, sourceName, targetName);
  if (combat) return combat;

  if (!isMyTurn) {
    const who = currentPlayerName ?? 'Another player';
    return {
      primary: `${who}'s turn — wait for them to attack or pass.`,
      secondary: 'WAITING',
      tone: 'wait',
    };
  }

  // Stuck-state coaching takes priority over the generic execute-tests
  // hint. Both branches are scoped to execute-tests + idle + my-turn
  // by the parent (Play.tsx); we just trust the booleans here.
  if (inRescueDeployMode) {
    return {
      primary:
        'No attacks possible — tap one of your territories to deploy 1 cube.',
      secondary: `RESERVES: ${reserveCubes}`,
      tone: 'act',
    };
  }
  if (isHardStuck) {
    return {
      primary: 'No possible attacks · no reserves — end your turn.',
      secondary: 'STUCK',
      tone: 'wait',
    };
  }

  // execute-tests is the player-driven phase. The other three (draft-plan,
  // document-deploy, incident-fortify) auto-progress on the client — but
  // the auto-pilot can take 200-1500ms to land its dispatched action and
  // round-trip the server. During that window the user sees the map with
  // ZERO guidance and reasonably assumes they're stuck. So we show the
  // attack-flow hint for ALL of those phases too: it tells the player
  // what they'll be doing in 1-2 seconds anyway, eliminates the
  // "what now?" gap, and matches the simplified gameplay loop where the
  // only thing they ever do is click territories.
  if (
    phase === 'execute-tests' ||
    phase === 'draft-plan' ||
    phase === 'document-deploy' ||
    phase === 'incident-fortify'
  ) {
    // For the auto-piloted phases, name the current phase explicitly so
    // a first-time player understands the game is *doing something* (not
    // stuck) and which phase that is. We deliberately *omit* this for
    // `execute-tests` — there the primary line already describes the
    // active phase, so a "now: Attack Phase…" echo would be redundant.
    const nowLine = NOW_LINE_BY_PHASE[phase];
    const base: CoachMessage = {
      primary:
        'Your turn — click one of YOUR territories (with 2+ cubes) to attack from. Stronger territories make stronger attacks.',
      secondary: 'STEP 1 OF 3',
      tone: 'act',
    };
    return nowLine ? { ...base, nowLine } : base;
  }

  return null;
}

// What's-happening-right-now copy for the auto-piloted phases. Mirrors the
// PhaseTransitionToast's PHASE_EXPLANATIONS but pre-pends the phase title
// because this line lives alongside a forward-looking action hint and the
// player needs to disambiguate "what's happening" from "what I should do".
const NOW_LINE_BY_PHASE: Readonly<Partial<Record<TurnPhase, string>>> = {
  'draft-plan':
    'Getting your cubes ready — placing reinforcements on your weakest territory.',
  'document-deploy':
    'Setting up your turn… almost ready to attack!',
  'incident-fortify':
    'Tidying up your turn… hang tight!',
};

function combatCopy(
  attack: AttackState,
  sourceName: string,
  targetName: string,
): CoachMessage | null {
  const src = sourceName || 'the selected territory';
  const tgt = targetName || 'the target';

  switch (attack.kind) {
    case 'idle':
      return null;
    case 'sourceSelected':
      return {
        primary: `Attacking from ${src}. Now click a pulsing enemy territory next to it — that's who you'll invade. (Click ${src} again to cancel.)`,
        secondary: 'STEP 2 OF 3',
        tone: 'combat',
      };
    case 'targetSelected':
      return {
        primary: `Attacking ${tgt} from ${src}. Commit cubes — each cube buys one quiz attempt. More cubes = more chances to win, but you must keep at least 1 cube home.`,
        secondary: 'STEP 3 OF 3',
        tone: 'combat',
      };
    case 'combatActive':
      return {
        primary: `Combat: ${src} → ${tgt}. Answer correctly to score a HIT (defender loses a cube). Wrong answer = YOU lose a cube.`,
        secondary: `TIER ${attack.question.tier} QUESTION`,
        tone: 'combat',
      };
    case 'combatResolved':
      return attack.outcome === 'hit'
        ? {
            primary: `HIT! ${tgt} lost a cube. Press the attack again, or stop here and keep what you've taken.`,
            secondary: 'CONTINUE OR STOP',
            tone: 'combat',
          }
        : {
            primary: `Miss — YOU lost a cube. Try again, or back off if ${src} is getting too thin.`,
            secondary: 'CONTINUE OR STOP',
            tone: 'combat',
          };
    case 'territoryConquered':
      return {
        primary: `Victory — ${tgt} is yours! Move cubes from ${src} to hold it (at least ${attack.mustMoveAtLeast}, up to ${attack.mustMoveAtMost}).`,
        secondary: `MOVE ${attack.mustMoveAtLeast}–${attack.mustMoveAtMost}`,
        tone: 'combat',
      };
  }
}
